import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ArrowLeft } from 'lucide-react';
import { platformAPI } from '../lib/api';

const FAQ = () => {
  const navigate = useNavigate();
  const [platformSettings, setPlatformSettings] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await platformAPI.getSettings();
        setPlatformSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch platform settings');
      }
    };
    fetchSettings();
  }, []);

  // Extract values from settings or use defaults
  const tradingRules = platformSettings?.trading_rules || {
    max_daily_loss: 5,
    max_total_loss: 10,
    max_floating_loss: 2,
    profit_target_phase1: 8,
    profit_target_phase2: 5
  };
  
  const payoutSettings = platformSettings?.payout_settings || {
    first_payout_days: 7,
    profit_split_base: 90
  };

  const faqs = [
    {
      question: 'How does the two-phase evaluation work?',
      answer: `Our evaluation consists of two phases. In Phase 1, you need to reach a ${tradingRules.profit_target_phase1}% profit target while respecting our risk management rules. Once you pass, Phase 2 requires a ${tradingRules.profit_target_phase2}% profit target. After passing both phases, you'll receive a funded account with real capital to trade.`
    },
    {
      question: 'What are the trading rules?',
      answer: `The main rules are: Maximum ${tradingRules.max_daily_loss}% daily loss, Maximum ${tradingRules.max_total_loss}% total drawdown, Maximum ${tradingRules.max_floating_loss}% floating loss at any time, and achieving the profit target for each phase. There's no minimum trading days requirement and no time limit to complete the challenges.`
    },
    {
      question: 'What is the free retake policy?',
      answer: 'First-time buyers receive one free retake if they fail their challenge. This means if you don\'t pass on your first attempt, you can restart the same challenge at no additional cost. This offer applies to your first purchase only.'
    },
    {
      question: 'How much of the profits do I keep?',
      answer: `Funded traders receive up to ${payoutSettings.profit_split_base}% of the profits they generate. The profit split starts at 80% and can increase based on your performance and consistency. With the upgrade option, you can get up to 100%.`
    },
    {
      question: 'How fast are payouts processed?',
      answer: `Your first payout is available after ${payoutSettings.first_payout_days} trading days. Payout requests are typically processed within 24-48 hours. We support multiple payment methods including bank transfer, PayPal, and cryptocurrency.`
    },
    {
      question: 'What trading instruments can I trade?',
      answer: 'You can trade Forex pairs, indices, commodities, and cryptocurrencies. The full list of available instruments will be provided upon receiving your funded account credentials.'
    },
    {
      question: 'Is there a minimum trading days requirement?',
      answer: 'No, there\'s no minimum trading days requirement to complete the challenge. You can complete the challenge at your own pace. However, we encourage consistent trading habits as they\'re key to long-term success.'
    },
    {
      question: 'Can I use Expert Advisors (EAs) or automated trading?',
      answer: 'Yes, you can use EAs and automated trading systems. However, they must comply with our risk management rules and be your own proprietary systems. Copy trading from other prop firm accounts is not allowed.'
    },
    {
      question: 'What happens if I breach the rules?',
      answer: `If you breach any of the trading rules (${tradingRules.max_daily_loss}% daily loss, ${tradingRules.max_total_loss}% total drawdown, or ${tradingRules.max_floating_loss}% floating loss), your challenge will be marked as failed. If you're a first-time buyer with a free retake available, you can restart the challenge at no cost.`
    },
    {
      question: 'What is the floating loss rule?',
      answer: `Traders cannot exceed a ${tradingRules.max_floating_loss}% floating loss (unrealized loss on open positions) at any given time during trading. Exceeding this limit will result in an automatic breach of your account. This rule applies to both challenge and funded accounts.`
    },
    {
      question: 'What is the Minimum Trading Days requirement?',
      answer: `You must actively trade for at least 6 days before requesting your first payout. This ensures consistent trading behavior - you cannot simply make a large profit on day 1 and then stop trading while waiting to request a payout. A "trading day" counts as any day where you open or close at least one position. This rule is standard across most prop firms and promotes disciplined, regular trading activity.`
    },
    {
      question: 'Is there a time limit to complete the challenge?',
      answer: `No! All our accounts have unlimited time - there are no deadlines on any of our Evaluation Challenges, Instant Funded accounts, or Elite Funded accounts. Take as long as you need to reach your profit targets while respecting the trading rules. Trade at your own pace without the pressure of time limits.`
    },
    {
      question: 'How do I get started?',
      answer: 'Simply create an account, choose your preferred account size, complete the purchase, and you\'ll receive your trading credentials via email. You can start trading immediately!'
    }
  ];

  return (
    <div className="min-h-screen bg-[hsl(220,20%,4%)] pt-20" data-testid="faq-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back to Dashboard Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="mb-6 border-white/10 text-white hover:bg-white/5"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="text-center mb-12"
        >
          <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight mb-4 text-white" data-testid="faq-title">
            FREQUENTLY ASKED <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">QUESTIONS</span>
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Everything you need to know about TRADERSCAPITAL and how to become a funded trader.
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-card border-border">
            <CardContent className="p-6">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, i) => (
                  <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
                    <AccordionTrigger className="text-left font-heading text-lg hover:text-primary">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </motion.div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-12 text-center"
        >
          <p className="text-muted-foreground">
            Still have questions? Contact us at{' '}
            <a href="mailto:support@tradercapital.com" className="text-primary hover:underline">
              support@tradercapital.com
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;
